/**
 * BLRRD Pointwise Filters — Threshold
 *
 * Binary cut-off filter. Standard photo-editing math: every pixel
 * above the threshold becomes white, every pixel at or below becomes
 * black. Per-channel mode treats R/G/B independently; luminance mode
 * collapses to a single decision per pixel.
 *
 * Pipeline slot: POST-VIEW-TRANSFORM. Threshold is intrinsically
 * display-referred — the user picks a threshold in the same space
 * they're looking at on the monitor. Caller passes the post-view-
 * transform buffer (display-linear in [0,1] after filmic / sigmoid).
 *
 * Identity short-circuit: not applicable — threshold has no identity
 * state by definition. Caller must skip the filter entirely when the
 * user hasn't enabled it.
 *
 * Default threshold matches Photoshop / Lightroom: 0.5 (mid-grey).
 */

/** Default Threshold params. */
export function defaultThresholdParams() {
    return { threshold: 0.5, channel: 'luminance' };
}

/**
 * True iff applying this filter would be a no-op. Threshold has no
 * mathematical identity — we return false always so the caller's
 * "enabled" toggle is the only opt-out. This matches the framework
 * contract: identity short-circuit gates buffer copies, not user
 * intent.
 */
export function isIdentityThreshold(_params) {
    return false;
}

/**
 * Apply a Threshold to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { threshold, channel }
 * @param {object}       [opts]      { selectionMask?: Float32Array }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applyThreshold(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyThreshold: selectionMask length mismatch');
    }
    const t = (params && params.threshold != null) ? params.threshold : 0.5;
    const channel = (params && params.channel) || 'luminance';
    const N = w * h;

    if (channel === 'luminance') {
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            /* Rec.709 luminance for display-referred input. */
            const Y = 0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2];
            const bit = Y >= t ? 1 : 0;
            if (m >= 1) {
                rgba[i] = bit;
                rgba[i + 1] = bit;
                rgba[i + 2] = bit;
            } else {
                const inv = 1 - m;
                rgba[i]     = inv * rgba[i]     + m * bit;
                rgba[i + 1] = inv * rgba[i + 1] + m * bit;
                rgba[i + 2] = inv * rgba[i + 2] + m * bit;
            }
        }
    } else {
        /* Per-channel. */
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const r = rgba[i]     >= t ? 1 : 0;
            const g = rgba[i + 1] >= t ? 1 : 0;
            const b = rgba[i + 2] >= t ? 1 : 0;
            if (m >= 1) {
                rgba[i] = r;
                rgba[i + 1] = g;
                rgba[i + 2] = b;
            } else {
                const inv = 1 - m;
                rgba[i]     = inv * rgba[i]     + m * r;
                rgba[i + 1] = inv * rgba[i + 1] + m * g;
                rgba[i + 2] = inv * rgba[i + 2] + m * b;
            }
        }
    }
    return rgba;
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyThreshold: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyThreshold: rgba.length !== w*h*4');
    }
}
